import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";
import { ClipboardCheck, Search, RefreshCw } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useInspections from "hooks/useInspections";
import { Inspection } from "services/inspection.service";

const Inspections = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = useInspections({ page, limit: 20 });

  const inspections: Inspection[] = response?.data?.data ?? [];

  // Followed here from an inspection alert. This list has no per-record route, so the id
  // arrives as `?focus=` and rings the row it names.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    inspections,
    isLoading
  );
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const filtered = search
    ? inspections.filter((ins) => {
        const q = search.toLowerCase();
        const myndaUser = ins.mynda?.user;
        const agencyName = ins.agency?.name ?? "";
        return (
          myndaUser?.firstName?.toLowerCase().includes(q) ||
          myndaUser?.lastName?.toLowerCase().includes(q) ||
          agencyName.toLowerCase().includes(q) ||
          ins.message?.toLowerCase().includes(q)
        );
      })
    : inspections;

  return (
    <React.Fragment>
      <BreadCrumb title="Inspections" pageTitle="Agency" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by worker, agency or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Mynda (Worker)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduled Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Message</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={5} label="Couldn't load inspections" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ClipboardCheck className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No inspections found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((ins) => (
                  <tr
                    key={ins._id}
                    ref={scrollRowIntoView(isFocused(ins._id))}
                    className={`hover:bg-gray-50 transition-colors ${focusRowClass(
                      isFocused(ins._id)
                    )}`}
                  >
                    <td data-label="Mynda (Worker)" data-cell="title" className="px-4 py-3">
                      {ins.mynda?.user ? (
                        <>
                          <p className="font-medium text-gray-900">
                            {ins.mynda.user.firstName} {ins.mynda.user.lastName}
                          </p>
                          <p className="text-xs text-gray-400">{ins.mynda.user.phoneNumber}</p>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td data-label="Agency" data-cell="block" className="px-4 py-3">
                      <p className="text-gray-700">{ins.agency?.name ?? "—"}</p>
                      {ins.agency?.companyEmail && (
                        <p className="text-xs text-gray-400">{ins.agency.companyEmail}</p>
                      )}
                    </td>
                    <td data-label="Scheduled Date" className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {ins.dateTime
                        ? new Date(ins.dateTime).toLocaleString()
                        : "—"}
                    </td>
                    <td data-label="Message" data-cell="block" className="px-4 py-3 text-gray-600 sm:max-w-xs">
                      <p className="truncate">{ins.message || "—"}</p>
                    </td>
                    <td data-label="Requested" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(ins.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Inspections;
